import React, { useCallback, useEffect, useRef, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import './custom.css';
import {
  accessTokenStorageKey,
  createFactorDefinition,
  createProject as createProjectApi,
  createProjectClass,
  deleteFactorDefinition,
  deleteProject as deleteProjectApi,
  deleteProjectClass,
  deleteProjectGrade,
  deleteRun,
  updateFactorSetting as updateFactorSettingApi,
  generateProposal,
  loginToApi,
  logoutFromApi,
  renameRun,
  requestClasses,
  requestDashboard,
  requestFactors,
  requestGrades,
  requestProjects,
  requestRoster,
  requestSavedRuns,
  restoreSessionFromApi,
  updateFactorDefinition,
  updateProjectClass,
  updateStudentRelationships,
  updateRosterStudent,
  createProjectGrade,
  createRosterStudent,
  deleteRosterStudent,
  importRoster,
  updateProjectGrade,
  updateProjectSettings,
  setTokenRefreshCallback,
  requestUsers,
  createUserApi,
  updateUserApi,
  deleteUserApi,
  markTutorialSeenApi,
  requestAdminSchools,
  createAdminSchool,
  updateAdminSchool,
  deleteAdminSchool,
  reseedAdminSchool,
  requestAdminUsers,
  updateAdminUserRole,
  resetAdminUserPassword,
  assignAdminUserToSchool,
  removeAdminUserFromSchool,
  createAdminUserApi,
  moveAdminUserToSchool,
  updateSchoolUserRoleApi,
  assignStudentApi,
  toggleStudentLockApi,
  toggleStudentBehaviorApi,
  applyRunAssignmentsApi,
  requestMagicLinkApi,
  verifyMagicLinkApi
} from './api/client';
import { filterOptions, navigationSections } from './constants/navigation';
import { getTerminology } from './utils/terminology';
import ClassesView from './views/ClassesView';
import FactorsView from './views/FactorsView';
import GradesView from './views/GradesView';
import HomeView from './views/HomeView';
import OverviewView from './views/OverviewView';
import PlaceholderView from './views/PlaceholderView';
import RosterView from './views/RosterView';
import SavedRunsView from './views/SavedRunsView';
import UsersView from './views/UsersView';
import TutorialOverlay from './components/TutorialOverlay';
import ChatBubble from './components/ChatBubble';
import AdminView from './views/AdminView';
import { analytics } from './analytics';

const placeholderRoutes = [];

function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [roster, setRoster] = useState(null);
  const [classesWorkspace, setClassesWorkspace] = useState(null);
  const [savedRunsWorkspace, setSavedRunsWorkspace] = useState(null);
  const [user, setUser] = useState(null);
  const [accessToken, setAccessToken] = useState('');

  useEffect(() => {
    setTokenRefreshCallback((newToken) => {
      setAccessToken(newToken);
    });
    return () => setTokenRefreshCallback(null);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const authError = params.get('auth_error');
    if (authError) {
      setLoginError(decodeURIComponent(authError));
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [generationError, setGenerationError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [activeFilter, setActiveFilter] = useState('all');
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const [loginMode, setLoginMode] = useState('default'); // 'default' | 'password' | 'magic-link'
  const [magicLinkSent, setMagicLinkSent] = useState(false);
  const [magicLinkEmail, setMagicLinkEmail] = useState('');
  const [isRosterLoading, setIsRosterLoading] = useState(false);
  const [rosterError, setRosterError] = useState('');
  const [isSavingRoster, setIsSavingRoster] = useState(false);
  const [rosterSaveError, setRosterSaveError] = useState('');
  const [isClassesLoading, setIsClassesLoading] = useState(false);
  const [classesError, setClassesError] = useState('');
  const [isSavingClasses, setIsSavingClasses] = useState(false);
  const [classesSaveError, setClassesSaveError] = useState('');
  const [gradesWorkspace, setGradesWorkspace] = useState(null);
  const [isGradesLoading, setIsGradesLoading] = useState(false);
  const [, setGradesError] = useState('');
  const [isSavingGrades, setIsSavingGrades] = useState(false);
  const [gradesSaveError, setGradesSaveError] = useState('');
  const [isSavedRunsLoading, setIsSavedRunsLoading] = useState(false);
  const [savedRunsError, setSavedRunsError] = useState('');
  const [factorsWorkspace, setFactorsWorkspace] = useState(null);
  const [isFactorsLoading, setIsFactorsLoading] = useState(false);
  const [factorsError, setFactorsError] = useState('');
  const [isSavingFactors, setIsSavingFactors] = useState(false);
  const [factorsSaveError, setFactorsSaveError] = useState('');
  const [usersWorkspace, setUsersWorkspace] = useState(null);
  const [isUsersLoading, setIsUsersLoading] = useState(false);
  const [isSavingUser, setIsSavingUser] = useState(false);
  const [userSaveError, setUserSaveError] = useState('');
  const [adminWorkspace, setAdminWorkspace] = useState(null);
  const [isAdminLoading, setIsAdminLoading] = useState(false);
  const [isSavingAdmin, setIsSavingAdmin] = useState(false);
  const [adminSaveError, setAdminSaveError] = useState('');
  const [showTutorial, setShowTutorial] = useState(false);
  const [solverBlockerMessage, setSolverBlockerMessage] = useState('');
  const [isGenerateDialogOpen, setIsGenerateDialogOpen] = useState(false);
  const [runNameDraft, setRunNameDraft] = useState('');
  const [projectsList, setProjectsList] = useState([]);
  const [isProjectSwitcherOpen, setIsProjectSwitcherOpen] = useState(false);
  const [isNewProjectDialogOpen, setIsNewProjectDialogOpen] = useState(false);
  const [newProjectName, setNewProjectName] = useState('');
  const [newProjectSchoolYear, setNewProjectSchoolYear] = useState('');
  const [newProjectSourceGrade, setNewProjectSourceGrade] = useState('');
  const [newProjectTargetGrade, setNewProjectTargetGrade] = useState('');
  const [isCreatingProject, setIsCreatingProject] = useState(false);
  const [projectError, setProjectError] = useState('');
  const [isSwitchingProject, setIsSwitchingProject] = useState(false);
  const [isAvatarMenuOpen, setIsAvatarMenuOpen] = useState(false);
  const avatarMenuRef = useRef(null);

  useEffect(() => {
    if (!isAvatarMenuOpen) return;
    function handleClickOutside(event) {
      if (avatarMenuRef.current && !avatarMenuRef.current.contains(event.target)) {
        setIsAvatarMenuOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAvatarMenuOpen]);

  const isOverviewRoute = location.pathname === '/overview';
  const isRosterRoute = location.pathname === '/roster';
  const isGradesRoute = location.pathname === '/grades';
  const isClassesRoute = location.pathname === '/classes' || location.pathname === '/bunks';
  const isSavedRunsRoute = location.pathname.startsWith('/saved-runs');
  const isFactorsRoute = location.pathname === '/factors';
  const isUsersRoute = location.pathname === '/users';
  const isAdminRoute = location.pathname === '/admin';
  const savedRunsRouteRunId = isSavedRunsRoute
    ? location.pathname.replace('/saved-runs', '').replace(/^\//, '') || undefined
    : undefined;
  const activeProjectId = dashboard?.project?.id;
  const activeRosterProjectId = roster?.project?.id;
  const activeClassesProjectId = classesWorkspace?.project?.id;
  const activeGradesProjectId = gradesWorkspace?.project?.id;
  const activeSavedRunsProjectId = savedRunsWorkspace?.project?.id;
  const activeSavedRunId = savedRunsWorkspace?.selectedRun?.id;
  const terminology = getTerminology(dashboard?.school?.schoolType);
  const hasBootstrappedRef = useRef(false);
  const projectSwitcherRef = useRef(null);
  const previousGenerationIdRef = useRef(null);

  useEffect(() => {
    if (activeProjectId) {
      document.cookie = `ca_project_id=${encodeURIComponent(activeProjectId)};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
    }
  }, [activeProjectId]);

  useEffect(() => {
    const path = location.pathname;
    if (path && path !== '/') {
      document.cookie = `ca_last_route=${encodeURIComponent(path)};path=/;max-age=${60 * 60 * 24 * 365};SameSite=Lax`;
    }
  }, [location.pathname]);

  const persistSession = useCallback((sessionPayload) => {
    localStorage.setItem(accessTokenStorageKey, sessionPayload.accessToken);
    setAccessToken(sessionPayload.accessToken);
    setUser(sessionPayload.user);
    if (sessionPayload.user && !sessionPayload.user.hasSeenTutorial) {
      setShowTutorial(true);
    }
  }, []);

  const resetWorkspaceState = useCallback(() => {
    setDashboard(null);
    setRoster(null);
    setClassesWorkspace(null);
    setGradesWorkspace(null);
    setSavedRunsWorkspace(null);
    setFactorsWorkspace(null);
    setGenerationError('');
    setRosterError('');
    setRosterSaveError('');
    setClassesError('');
    setClassesSaveError('');
    setSavedRunsError('');
    setIsGenerating(false);
    setIsGenerateDialogOpen(false);
    setRunNameDraft('');
    setIsProjectSwitcherOpen(false);
    setProjectError('');
  }, []);

  const clearStoredSession = useCallback(() => {
    localStorage.removeItem(accessTokenStorageKey);
    setAccessToken('');
    setUser(null);
    resetWorkspaceState();
  }, [resetWorkspaceState]);

  const classifyAuthenticatedRequestError = useCallback(
    (requestError) => {
      if (requestError?.name === 'AbortError') {
        return 'abort';
      }

      if (requestError?.status === 401) {
        clearStoredSession();
        return 'unauthorized';
      }

      return 'error';
    },
    [clearStoredSession]
  );

  const loadRoster = useCallback(async (token, projectId, signal) => {
    setIsRosterLoading(true);
    setRosterError('');

    try {
      const rosterPayload = await requestRoster(token, projectId, signal);
      setRoster(rosterPayload);
      return rosterPayload;
    } finally {
      setIsRosterLoading(false);
    }
  }, []);

  const loadClasses = useCallback(async (token, projectId, signal) => {
    setIsClassesLoading(true);
    setClassesError('');

    try {
      const classesPayload = await requestClasses(token, projectId, signal);
      setClassesWorkspace(classesPayload);
      return classesPayload;
    } finally {
      setIsClassesLoading(false);
    }
  }, []);

  const loadGrades = useCallback(async (token, projectId, signal) => {
    setIsGradesLoading(true);
    setGradesError('');

    try {
      const gradesPayload = await requestGrades(token, projectId, signal);
      setGradesWorkspace(gradesPayload);
      return gradesPayload;
    } finally {
      setIsGradesLoading(false);
    }
  }, []);

  const loadSavedRuns = useCallback(async (token, projectId, runId, signal) => {
    setIsSavedRunsLoading(true);
    setSavedRunsError('');

    try {
      const runsPayload = await requestSavedRuns(token, projectId, runId, signal);
      setSavedRunsWorkspace(runsPayload);
      return runsPayload;
    } finally {
      setIsSavedRunsLoading(false);
    }
  }, []);

  const loadFactors = useCallback(async (token, projectId, signal) => {
    setIsFactorsLoading(true);
    setFactorsError('');

    try {
      const factorsPayload = await requestFactors(token, projectId, signal);
      setFactorsWorkspace(factorsPayload);
      return factorsPayload;
    } finally {
      setIsFactorsLoading(false);
    }
  }, []);

  const refreshDashboard = useCallback(async (token, projectId, signal) => {
    try {
      const dashboardPayload = await requestDashboard(token, projectId, signal);
      setDashboard(dashboardPayload);
      return dashboardPayload;
    } catch (requestError) {
      if (requestError?.status === 404 && projectId) {
        try {
          const fallback = await requestDashboard(token, undefined, signal);
          setDashboard(fallback);
          return fallback;
        } catch (fallbackError) {
          if (fallbackError?.status === 404) {
            setDashboard(null);
            return null;
          }
          throw fallbackError;
        }
      }
      if (requestError?.status === 404) {
        setDashboard(null);
        return null;
      }
      throw requestError;
    }
  }, []);

  const loadProjects = useCallback(async (token, signal) => {
    try {
      const payload = await requestProjects(token, signal);
      setProjectsList(payload.projects ?? []);
    } catch (requestError) {
      if (requestError?.name !== 'AbortError') {
        setProjectsList([]);
      }
    }
  }, []);

  const refreshLoadedWorkspaces = useCallback(
    async (
      token,
      projectId,
      {
        includeDashboard = true,
        includeRoster = activeRosterProjectId === projectId,
        includeClasses = activeClassesProjectId === projectId,
        includeGrades = activeGradesProjectId === projectId,
        includeSavedRuns = activeSavedRunsProjectId === projectId,
        signal
      } = {}
    ) => {
      const tasks = [];

      if (includeDashboard) {
        tasks.push(refreshDashboard(token, projectId, signal));
      }

      if (includeRoster) {
        tasks.push(loadRoster(token, projectId, signal));
      }

      if (includeClasses) {
        tasks.push(loadClasses(token, projectId, signal));
      }

      if (includeGrades) {
        tasks.push(loadGrades(token, projectId, signal));
      }

      if (includeSavedRuns) {
        tasks.push(loadSavedRuns(token, projectId, activeSavedRunId, signal));
      }

      await Promise.all(tasks);
    },
    [
      activeClassesProjectId,
      activeGradesProjectId,
      activeRosterProjectId,
      activeSavedRunId,
      activeSavedRunsProjectId,
      loadClasses,
      loadGrades,
      loadRoster,
      loadSavedRuns,
      refreshDashboard
    ]
  );

  const loadWorkspace = useCallback(
    async (
      token,
      {
        projectId,
        includeRoster = isRosterRoute,
        includeClasses = isClassesRoute,
        includeGrades = isGradesRoute,
        includeSavedRuns = isSavedRunsRoute,
        savedRunsRunId = savedRunsRouteRunId,
        signal
      } = {}
    ) => {
      const dashboardPayload = await refreshDashboard(token, projectId, signal);

      if (includeRoster && dashboardPayload.project?.id) {
        await loadRoster(token, dashboardPayload.project.id, signal);
      }

      if (includeClasses && dashboardPayload.project?.id) {
        await loadClasses(token, dashboardPayload.project.id, signal);
      }

      if (includeGrades && dashboardPayload.project?.id) {
        await loadGrades(token, dashboardPayload.project.id, signal);
      }

      if (includeSavedRuns && dashboardPayload.project?.id) {
        await loadSavedRuns(token, dashboardPayload.project.id, savedRunsRunId, signal);
      }

      return dashboardPayload;
    },
    [
      isClassesRoute,
      isGradesRoute,
      isRosterRoute,
      isSavedRunsRoute,
      loadClasses,
      loadGrades,
      loadRoster,
      loadSavedRuns,
      refreshDashboard,
      savedRunsRouteRunId
    ]
  );

  useEffect(() => {
    if (hasBootstrappedRef.current) {
      return undefined;
    }

    hasBootstrappedRef.current = true;
    const controller = new AbortController();

    function readCookie(name) {
      const match = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return match ? decodeURIComponent(match[1]) : null;
    }

    async function bootstrap() {
      try {
        // Check for magic link token in URL
        const params = new URLSearchParams(window.location.search);
        const magicToken = params.get('token');
        const isVerifyRoute = window.location.pathname.endsWith('/auth/verify');

        if (isVerifyRoute && magicToken) {
          try {
            const session = await verifyMagicLinkApi(magicToken, controller.signal);
            persistSession(session);
            analytics.identify(session.user.id, { email: session.user.email, role: session.user.platformRole });
            analytics.track('login_success', { method: 'magic_link' });
            navigate('/', { replace: true });
            window.history.replaceState({}, '', process.env.PUBLIC_URL || '/');
            await loadWorkspace(session.accessToken, {});
            await loadProjects(session.accessToken);
            return;
          } catch (magicLinkError) {
            setLoginError(magicLinkError.message || 'Magic link is invalid or expired.');
            navigate('/', { replace: true });
            window.history.replaceState({}, '', process.env.PUBLIC_URL || '/');
            clearStoredSession();
            return;
          }
        }

        const restoredSession = await restoreSessionFromApi(controller.signal);
        if (!restoredSession) {
          clearStoredSession();
          return;
        }

        const savedRoute = readCookie('ca_last_route');
        const savedProjectId = readCookie('ca_project_id');

        if (savedRoute && location.pathname === '/') {
          navigate(savedRoute, { replace: true });
        }

        persistSession(restoredSession);
        analytics.identify(restoredSession.user.id, { email: restoredSession.user.email, role: restoredSession.user.platformRole });
        await Promise.all([
          loadWorkspace(restoredSession.accessToken, {
            projectId: savedProjectId || undefined,
            includeClasses: isClassesRoute || savedRoute === '/classes' || savedRoute === '/bunks',
            includeGrades: isGradesRoute || savedRoute === '/grades',
            includeRoster: isRosterRoute || savedRoute === '/roster',
            includeSavedRuns: isSavedRunsRoute || (savedRoute && savedRoute.startsWith('/saved-runs')),
            savedRunsRunId: savedRunsRouteRunId,
            signal: controller.signal
          }),
          loadProjects(restoredSession.accessToken, controller.signal)
        ]);
        setError((currentError) => currentError || '');
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          clearStoredSession();
          setError(requestError.message || 'Unable to initialize the placement studio.');
        }
      } finally {
        setLoading(false);
      }
    }

    bootstrap();

    return () => controller.abort();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (
      !user ||
      !accessToken ||
      !activeProjectId ||
      !isRosterRoute ||
      activeRosterProjectId === activeProjectId
    ) {
      return undefined;
    }

    const controller = new AbortController();

    loadRoster(accessToken, activeProjectId, controller.signal).catch((requestError) => {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setRosterError(requestError.message || 'Unable to load the students workspace.');
      }
    });

    return () => controller.abort();
  }, [
    accessToken,
    activeProjectId,
    activeRosterProjectId,
    classifyAuthenticatedRequestError,
    isRosterRoute,
    loadRoster,
    user
  ]);

  useEffect(() => {
    if (
      !user ||
      !accessToken ||
      !activeProjectId ||
      !isSavedRunsRoute
    ) {
      return undefined;
    }

    const controller = new AbortController();

    loadSavedRuns(accessToken, activeProjectId, savedRunsRouteRunId, controller.signal).catch((requestError) => {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setSavedRunsError(requestError.message || 'Unable to load saved runs.');
      }
    });

    return () => controller.abort();
  }, [
    accessToken,
    activeProjectId,
    activeSavedRunsProjectId,
    classifyAuthenticatedRequestError,
    isSavedRunsRoute,
    loadSavedRuns,
    savedRunsRouteRunId,
    user
  ]);

  useEffect(() => {
    if (
      !user ||
      !accessToken ||
      !activeProjectId ||
      !isClassesRoute ||
      activeClassesProjectId === activeProjectId
    ) {
      return undefined;
    }

    const controller = new AbortController();

    loadClasses(accessToken, activeProjectId, controller.signal).catch((requestError) => {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setClassesError(requestError.message || 'Unable to load the classes workspace.');
      }
    });

    return () => controller.abort();
  }, [
    accessToken,
    activeClassesProjectId,
    activeProjectId,
    classifyAuthenticatedRequestError,
    isClassesRoute,
    loadClasses,
    user
  ]);

  useEffect(() => {
    if (
      !user ||
      !accessToken ||
      !activeProjectId ||
      !isGradesRoute ||
      activeGradesProjectId === activeProjectId
    ) {
      return undefined;
    }

    const controller = new AbortController();

    loadGrades(accessToken, activeProjectId, controller.signal).catch((requestError) => {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setGradesError(requestError.message || 'Unable to load the grades workspace.');
      }
    });

    return () => controller.abort();
  }, [
    accessToken,
    activeGradesProjectId,
    activeProjectId,
    classifyAuthenticatedRequestError,
    isGradesRoute,
    loadGrades,
    user
  ]);

  useEffect(() => {
    if (
      !user ||
      !accessToken ||
      !activeProjectId ||
      !isFactorsRoute ||
      factorsWorkspace !== null
    ) {
      return undefined;
    }

    const controller = new AbortController();

    loadFactors(accessToken, activeProjectId, controller.signal).catch((requestError) => {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setFactorsError(requestError.message || 'Unable to load factor definitions.');
      }
    });

    return () => controller.abort();
  }, [
    accessToken,
    activeProjectId,
    classifyAuthenticatedRequestError,
    factorsWorkspace,
    isFactorsRoute,
    loadFactors,
    user
  ]);

  useEffect(() => {
    if (!user || !accessToken || !isUsersRoute || !dashboard?.school?.id || usersWorkspace !== null) {
      return undefined;
    }

    loadUsers();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accessToken, isUsersRoute, dashboard?.school?.id, usersWorkspace]);

  useEffect(() => {
    if (!user || !accessToken || !isAdminRoute || user.platformRole !== 'platform_admin' || adminWorkspace !== null) {
      return undefined;
    }

    loadAdminData();
    return undefined;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, accessToken, isAdminRoute, adminWorkspace]);

  useEffect(() => {
    if (!user || !accessToken || !activeProjectId || !dashboard?.activeGeneration?.id) {
      return undefined;
    }

    const controller = new AbortController();
    let timeoutId = null;
    let cancelled = false;

    async function pollDashboard() {
      try {
        const dashboardPayload = await refreshDashboard(accessToken, activeProjectId, controller.signal);

        if (cancelled) {
          return;
        }

        setDashboard(dashboardPayload);
        setGenerationError('');

        if (dashboardPayload.activeGeneration?.id) {
          timeoutId = window.setTimeout(pollDashboard, 1500);
        }
      } catch (requestError) {
        if (cancelled) {
          return;
        }

        if (classifyAuthenticatedRequestError(requestError) !== 'error') {
          return;
        }

        setGenerationError(
          requestError.message || 'Unable to refresh the current proposal generation status.'
        );
        timeoutId = window.setTimeout(pollDashboard, 2500);
      }
    }

    timeoutId = window.setTimeout(pollDashboard, 1500);

    return () => {
      cancelled = true;
      controller.abort();
      if (timeoutId) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [
    accessToken,
    activeProjectId,
    classifyAuthenticatedRequestError,
    dashboard?.activeGeneration?.id,
    refreshDashboard,
    user
  ]);

  useEffect(() => {
    const currentGenerationId = dashboard?.activeGeneration?.id ?? null;
    const previousId = previousGenerationIdRef.current;
    previousGenerationIdRef.current = currentGenerationId;

    if (previousId && !currentGenerationId && accessToken && activeProjectId) {
      loadSavedRuns(accessToken, activeProjectId, previousId).catch(() => {});
      navigate(`/saved-runs/${previousId}`);
    }
  }, [dashboard?.activeGeneration?.id, accessToken, activeProjectId, loadSavedRuns, navigate]);

  useEffect(() => {
    if (!isProjectSwitcherOpen) {
      return undefined;
    }

    function handleClickOutside(event) {
      if (projectSwitcherRef.current && !projectSwitcherRef.current.contains(event.target)) {
        setIsProjectSwitcherOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProjectSwitcherOpen]);

  // Auto-select grade when dashboard loads
  const availableGrades = dashboard?.grades ?? [];
  const effectiveGrade = (() => {
    if (availableGrades.length === 1) return availableGrades[0].name;
    if (selectedGrade && availableGrades.some((g) => g.name === selectedGrade)) return selectedGrade;
    if (availableGrades.length > 0) return availableGrades[0].name;
    return null;
  })();

  const filteredStudents = dashboard
    ? dashboard.students.filter((student) => {
        const studentStatusKey = student.status.toLowerCase().replace(/\s+/g, '-');
        const matchesSearch =
          searchTerm.trim().length === 0 ||
          student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.studentId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          student.homeroom.toLowerCase().includes(searchTerm.toLowerCase());

        const matchesFilter = activeFilter === 'all'
          || studentStatusKey === activeFilter;

        const matchesGrade = !effectiveGrade || student.gradeLevel === effectiveGrade;

        return matchesSearch && matchesFilter && matchesGrade;
      })
    : [];

  const filteredClasses = dashboard
    ? dashboard.classes.filter((c) => !effectiveGrade || c.gradeLevel === effectiveGrade)
    : [];

  async function handleLoginSubmit(event) {
    event.preventDefault();
    setIsAuthenticating(true);
    setLoginError('');
    setError('');

    try {
      const session = await loginToApi(
        {
          email: loginEmail,
          password: loginPassword
        }
      );

      persistSession(session);
      analytics.identify(session.user.id, { email: session.user.email, role: session.user.platformRole });
      analytics.track('login_success', { method: 'password' });
      await Promise.all([
        loadWorkspace(session.accessToken, {
          includeClasses: isClassesRoute,
          includeGrades: isGradesRoute,
          includeRoster: isRosterRoute,
          includeSavedRuns: isSavedRunsRoute
        }),
        loadProjects(session.accessToken)
      ]);
      setLoginPassword('');
      setError('');
    } catch (requestError) {
      analytics.track('login_failed', { method: 'password' });
      setLoginError(requestError.message || 'Unable to sign in.');
    } finally {
      setIsAuthenticating(false);
      setLoading(false);
    }
  }

  async function handleMagicLinkSubmit(event) {
    event.preventDefault();
    setIsAuthenticating(true);
    setLoginError('');

    try {
      await requestMagicLinkApi(magicLinkEmail);
      analytics.track('magic_link_requested');
      setMagicLinkSent(true);
    } catch (requestError) {
      setLoginError(requestError.message || 'Unable to send magic link.');
    } finally {
      setIsAuthenticating(false);
    }
  }

  function handleOpenGenerateDialog() {
    if (!dashboard || !accessToken) return;

    // Check for blockers — show the first applicable one
    if (!user?.permissions?.includes('run.generate')) {
      setSolverBlockerMessage('You do not have permission to run the solver. Contact your administrator.');
      return;
    }

    if (isGenerating || dashboard.activeGeneration?.id) {
      setSolverBlockerMessage('A solver run is already in progress. Please wait for it to finish.');
      return;
    }

    const studentCount = dashboard.students?.length ?? 0;
    const classCount = dashboard.classes?.length ?? 0;

    if (studentCount === 0) {
      setSolverBlockerMessage(`No ${terminology.students.toLowerCase()} have been added yet. Import or create ${terminology.students.toLowerCase()} before running the solver.`);
      return;
    }

    if (classCount === 0) {
      setSolverBlockerMessage(`No ${terminology.classes.toLowerCase()} have been defined yet. Create ${terminology.classes.toLowerCase()} before running the solver.`);
      return;
    }

    setGenerationError('');
    setRunNameDraft('');
    setIsGenerateDialogOpen(true);
  }

  function handleCloseGenerateDialog() {
    if (isGenerating) {
      return;
    }

    setIsGenerateDialogOpen(false);
    setRunNameDraft('');
  }

  async function handleGenerateClick() {
    if (!dashboard || isGenerating || !accessToken || dashboard.activeGeneration?.id) {
      return;
    }

    setIsGenerating(true);
    setGenerationError('');

    try {
      analytics.track('solver_run_started', { runName: runNameDraft.trim(), gradeLevel: effectiveGrade || undefined });
      const dashboardPayload = await generateProposal(accessToken, dashboard.project.id, {
        name: runNameDraft.trim(),
        gradeLevel: effectiveGrade || undefined
      });
      setDashboard(dashboardPayload);
      analytics.track('solver_run_completed', { runName: runNameDraft.trim(), gradeLevel: effectiveGrade || undefined });
      setGenerationError('');
      setIsGenerateDialogOpen(false);
      setRunNameDraft('');

      await refreshLoadedWorkspaces(accessToken, dashboard.project.id, {
        includeDashboard: false
      });
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setGenerationError(requestError.message || 'Unable to generate a new proposal.');
      }
    } finally {
      setIsGenerating(false);
    }
  }

  async function handleSaveRosterStudent(studentId, payload) {
    if (!accessToken || !roster?.project?.id) {
      return;
    }

    setIsSavingRoster(true);
    setRosterSaveError('');

    try {
      const updatedRoster = await updateRosterStudent(accessToken, roster.project.id, studentId, payload);
      setRoster(updatedRoster);
      analytics.track('student_updated', { studentId, name: payload.name });
      await refreshLoadedWorkspaces(accessToken, roster.project.id, {
        includeRoster: false
      });

      return updatedRoster;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setRosterSaveError(requestError.message || 'Unable to save student changes.');
      }
    } finally {
      setIsSavingRoster(false);
    }
  }

  async function handleUpdateStudentRelationships(studentId, relationshipType, relatedStudentIds) {
    if (!accessToken || !roster?.project?.id) {
      return null;
    }

    setIsSavingRoster(true);
    setRosterSaveError('');

    try {
      const updatedRoster = await updateStudentRelationships(
        accessToken,
        roster.project.id,
        studentId,
        relationshipType,
        { relatedStudentIds }
      );
      setRoster(updatedRoster);
      analytics.track('student_relationships_updated', { studentId, relationshipType });
      await refreshLoadedWorkspaces(accessToken, roster.project.id, {
        includeRoster: false
      });

      return updatedRoster;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setRosterSaveError(requestError.message || 'Unable to save relationship changes.');
      }
      return null;
    } finally {
      setIsSavingRoster(false);
    }
  }

  async function handleUpdateProjectSettings(settings) {
    if (!accessToken || !dashboard?.project?.id) {
      return;
    }

    try {
      const projectId = dashboard.project.id;
      await updateProjectSettings(accessToken, projectId, settings);
      analytics.track('project_settings_updated', settings);
      setRoster(null);
      await refreshLoadedWorkspaces(accessToken, projectId);
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        console.error('Failed to update project settings:', requestError.message);
      }
    }
  }

  // --- Planner Assignment Handlers ---

  async function handleAssignStudent(studentId, classId) {
    if (!accessToken || !activeProjectId) return null;
    try {
      const updatedDashboard = await assignStudentApi(accessToken, activeProjectId, studentId, classId);
      setDashboard(updatedDashboard);
      analytics.track('student_assigned', { studentId, classId });
      return updatedDashboard;
    } catch (requestError) {
      classifyAuthenticatedRequestError(requestError);
      return null;
    }
  }

  async function handleToggleLock(studentId, isLocked) {
    if (!accessToken || !activeProjectId) return null;
    try {
      const updatedDashboard = await toggleStudentLockApi(accessToken, activeProjectId, studentId, isLocked);
      if (updatedDashboard) {
        setDashboard(updatedDashboard);
      }
      analytics.track('student_lock_toggled', { studentId, isLocked });
      return updatedDashboard;
    } catch (requestError) {
      console.error('[handleToggleLock] Error:', requestError);
      classifyAuthenticatedRequestError(requestError);
      return null;
    }
  }

  async function handleToggleBehavior(studentId, behaviorFlag) {
    if (!accessToken || !activeProjectId) return null;
    try {
      const updatedDashboard = await toggleStudentBehaviorApi(accessToken, activeProjectId, studentId, behaviorFlag);
      setDashboard(updatedDashboard);
      analytics.track('student_behavior_toggled', { studentId, behaviorFlag });
      return updatedDashboard;
    } catch (requestError) {
      classifyAuthenticatedRequestError(requestError);
      return null;
    }
  }

  async function handleApplyRunAssignments(runId, studentIds) {
    if (!accessToken || !activeProjectId) return null;
    try {
      const updatedDashboard = await applyRunAssignmentsApi(accessToken, activeProjectId, runId, studentIds);
      setDashboard(updatedDashboard);
      analytics.track('run_applied', { runId });
      return updatedDashboard;
    } catch (requestError) {
      classifyAuthenticatedRequestError(requestError);
      return null;
    }
  }

  async function handleCreateRosterStudent(payload) {
    if (!accessToken || !roster?.project?.id) {
      return null;
    }

    setIsSavingRoster(true);
    setRosterSaveError('');

    try {
      const updatedRoster = await createRosterStudent(accessToken, roster.project.id, payload);
      setRoster(updatedRoster);
      analytics.track('student_created', { name: payload.name, gradeLevel: payload.gradeLevel, gender: payload.gender });
      await refreshLoadedWorkspaces(accessToken, roster.project.id, {
        includeRoster: false
      });
      return updatedRoster;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setRosterSaveError(requestError.message || 'Unable to create student.');
      }
      return null;
    } finally {
      setIsSavingRoster(false);
    }
  }

  async function handleDeleteRosterStudent(studentId) {
    if (!accessToken || !roster?.project?.id) {
      return null;
    }

    setIsSavingRoster(true);
    setRosterSaveError('');

    try {
      const updatedRoster = await deleteRosterStudent(accessToken, roster.project.id, studentId);
      setRoster(updatedRoster);
      analytics.track('student_deleted', { studentId });
      await refreshLoadedWorkspaces(accessToken, roster.project.id, {
        includeRoster: false
      });
      return updatedRoster;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setRosterSaveError(requestError.message || 'Unable to delete student.');
      }
      return null;
    } finally {
      setIsSavingRoster(false);
    }
  }

  async function handleImportRoster(fileContent, fileName, mode) {
    if (!accessToken || !roster?.project?.id) {
      return null;
    }

    setIsSavingRoster(true);
    setRosterSaveError('');

    try {
      const updatedRoster = await importRoster(accessToken, roster.project.id, fileContent, fileName, mode);
      setRoster(updatedRoster);
      analytics.track('roster_imported', { mode, fileName });
      await refreshLoadedWorkspaces(accessToken, roster.project.id, {
        includeRoster: false
      });
      return updatedRoster;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setRosterSaveError(requestError.message || 'Unable to import roster.');
      }
      return null;
    } finally {
      setIsSavingRoster(false);
    }
  }

  async function handleCreateProjectClass(payload) {
    const projectId = classesWorkspace?.project?.id ?? activeProjectId;
    if (!accessToken || !projectId) {
      return null;
    }

    setIsSavingClasses(true);
    setClassesSaveError('');

    try {
      const updatedClasses = await createProjectClass(accessToken, projectId, payload);
      setClassesWorkspace(updatedClasses);
      analytics.track('class_created', { name: payload.name, gradeId: payload.gradeId, capacity: payload.capacity });
      await refreshLoadedWorkspaces(accessToken, projectId, {
        includeClasses: false
      });

      return updatedClasses;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setClassesSaveError(requestError.message || 'Unable to create the class.');
      }
      return null;
    } finally {
      setIsSavingClasses(false);
    }
  }

  async function handleSaveProjectClass(classId, payload) {
    const projectId = classesWorkspace?.project?.id ?? activeProjectId;
    if (!accessToken || !projectId) {
      return null;
    }

    setIsSavingClasses(true);
    setClassesSaveError('');

    try {
      const updatedClasses = await updateProjectClass(accessToken, projectId, classId, payload);
      setClassesWorkspace(updatedClasses);
      analytics.track('class_updated', { classId, name: payload.name });
      await refreshLoadedWorkspaces(accessToken, projectId, {
        includeClasses: false
      });

      return updatedClasses;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setClassesSaveError(requestError.message || 'Unable to save class changes.');
      }
      return null;
    } finally {
      setIsSavingClasses(false);
    }
  }

  async function handleDeleteProjectClass(classId) {
    const projectId = classesWorkspace?.project?.id ?? activeProjectId;
    if (!accessToken || !projectId) {
      return null;
    }

    setIsSavingClasses(true);
    setClassesSaveError('');

    try {
      const updatedClasses = await deleteProjectClass(accessToken, projectId, classId);
      setClassesWorkspace(updatedClasses);
      analytics.track('class_deleted', { classId });
      await refreshLoadedWorkspaces(accessToken, projectId, {
        includeClasses: false
      });

      return updatedClasses;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setClassesSaveError(requestError.message || 'Unable to delete the class.');
      }
      throw requestError;
    } finally {
      setIsSavingClasses(false);
    }
  }

  async function handleCreateGrade(payload) {
    const projectId = gradesWorkspace?.project?.id ?? activeProjectId;
    if (!accessToken || !projectId) {
      return null;
    }

    setIsSavingGrades(true);
    setGradesSaveError('');

    try {
      const updatedGrades = await createProjectGrade(accessToken, projectId, payload);
      setGradesWorkspace(updatedGrades);
      analytics.track('grade_created', { name: payload.name });
      await refreshLoadedWorkspaces(accessToken, projectId, {
        includeGrades: false
      });

      return updatedGrades;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setGradesSaveError(requestError.message || 'Unable to create the grade.');
      }
      return null;
    } finally {
      setIsSavingGrades(false);
    }
  }

  async function handleSaveGrade(gradeId, payload) {
    const projectId = gradesWorkspace?.project?.id ?? activeProjectId;
    if (!accessToken || !projectId) {
      return null;
    }

    setIsSavingGrades(true);
    setGradesSaveError('');

    try {
      const updatedGrades = await updateProjectGrade(accessToken, projectId, gradeId, payload);
      setGradesWorkspace(updatedGrades);
      analytics.track('grade_updated', { gradeId, name: payload.name });
      await refreshLoadedWorkspaces(accessToken, projectId, {
        includeGrades: false
      });

      return updatedGrades;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setGradesSaveError(requestError.message || 'Unable to save grade changes.');
      }
      return null;
    } finally {
      setIsSavingGrades(false);
    }
  }

  async function handleDeleteGrade(gradeId) {
    const projectId = gradesWorkspace?.project?.id ?? activeProjectId;
    if (!accessToken || !projectId) {
      return null;
    }

    setIsSavingGrades(true);
    setGradesSaveError('');

    try {
      const updatedGrades = await deleteProjectGrade(accessToken, projectId, gradeId);
      setGradesWorkspace(updatedGrades);
      analytics.track('grade_deleted', { gradeId });
      await refreshLoadedWorkspaces(accessToken, projectId, {
        includeGrades: false
      });

      return updatedGrades;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setGradesSaveError(requestError.message || 'Unable to delete the grade.');
      }
      throw requestError;
    } finally {
      setIsSavingGrades(false);
    }
  }

  async function handleCreateFactor(payload) {
    if (!accessToken || !activeProjectId) return;
    setIsSavingFactors(true);
    setFactorsSaveError('');
    try {
      await createFactorDefinition(accessToken, activeProjectId, payload);
      const updated = await requestFactors(accessToken, activeProjectId);
      setFactorsWorkspace(updated);
      analytics.track('factor_created', { name: payload.name, type: payload.type });
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setFactorsSaveError(requestError.message || 'Unable to create the factor.');
      }
      throw requestError;
    } finally {
      setIsSavingFactors(false);
    }
  }

  async function handleUpdateFactor(definitionId, payload) {
    if (!accessToken || !activeProjectId) return;
    setIsSavingFactors(true);
    setFactorsSaveError('');
    try {
      await updateFactorDefinition(accessToken, activeProjectId, definitionId, payload);
      const updated = await requestFactors(accessToken, activeProjectId);
      setFactorsWorkspace(updated);
      analytics.track('factor_updated', { definitionId, name: payload.name });
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setFactorsSaveError(requestError.message || 'Unable to update the factor.');
      }
      throw requestError;
    } finally {
      setIsSavingFactors(false);
    }
  }

  async function handleUpdateFactorSetting(settingId, payload) {
    if (!accessToken || !activeProjectId) return;
    setIsSavingFactors(true);
    setFactorsSaveError('');
    try {
      const updated = await updateFactorSettingApi(accessToken, activeProjectId, settingId, payload);
      setFactorsWorkspace(updated);
      analytics.track('factor_setting_updated', { settingId });
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setFactorsSaveError(requestError.message || 'Unable to update the factor setting.');
      }
      throw requestError;
    } finally {
      setIsSavingFactors(false);
    }
  }

  async function handleDeleteFactor(definitionId) {
    if (!accessToken || !activeProjectId) return;
    setIsSavingFactors(true);
    setFactorsSaveError('');
    try {
      await deleteFactorDefinition(accessToken, activeProjectId, definitionId);
      const updated = await requestFactors(accessToken, activeProjectId);
      setFactorsWorkspace(updated);
      analytics.track('factor_deleted', { definitionId });
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setFactorsSaveError(requestError.message || 'Unable to delete the factor.');
      }
      throw requestError;
    } finally {
      setIsSavingFactors(false);
    }
  }

  // --- User Management ---

  async function loadUsers() {
    if (!accessToken || !dashboard?.school?.id) return;
    setIsUsersLoading(true);
    try {
      const usersPayload = await requestUsers(accessToken, dashboard.school.id);
      setUsersWorkspace(usersPayload);
    } catch (requestError) {
      classifyAuthenticatedRequestError(requestError);
    } finally {
      setIsUsersLoading(false);
    }
  }

  async function handleCreateUser(payload) {
    if (!accessToken || !dashboard?.school?.id) return;
    setIsSavingUser(true);
    setUserSaveError('');
    try {
      const result = await createUserApi(accessToken, { ...payload, schoolId: dashboard.school.id });
      setUsersWorkspace(result);
      analytics.track('user_created', { email: payload.email, role: payload.role });
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setUserSaveError(requestError.message || 'Unable to create user.');
      }
      return null;
    } finally {
      setIsSavingUser(false);
    }
  }

  async function handleUpdateUser(userId, payload) {
    if (!accessToken || !dashboard?.school?.id) return;
    setIsSavingUser(true);
    setUserSaveError('');
    try {
      const result = await updateUserApi(accessToken, userId, { ...payload, schoolId: dashboard.school.id });
      setUsersWorkspace(result);
      analytics.track('user_updated', { userId });
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setUserSaveError(requestError.message || 'Unable to update user.');
      }
      return null;
    } finally {
      setIsSavingUser(false);
    }
  }

  async function handleRemoveUser(userId) {
    if (!accessToken || !dashboard?.school?.id) return;
    setIsSavingUser(true);
    setUserSaveError('');
    try {
      const result = await deleteUserApi(accessToken, userId, dashboard.school.id);
      setUsersWorkspace(result);
      analytics.track('user_deleted', { userId });
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setUserSaveError(requestError.message || 'Unable to remove user.');
      }
      return null;
    } finally {
      setIsSavingUser(false);
    }
  }

  // --- Tutorial ---

  async function handleTutorialComplete() {
    setShowTutorial(false);
    try {
      await markTutorialSeenApi(accessToken);
      analytics.track('tutorial_completed');
      setUser((prev) => prev ? { ...prev, hasSeenTutorial: true } : prev);
    } catch {
      // Non-critical — tutorial will show again next login if this fails
    }
  }

  // --- Platform Admin ---

  async function loadAdminData() {
    if (!accessToken) return;
    setIsAdminLoading(true);
    try {
      const [schoolsResult, usersResult] = await Promise.all([
        requestAdminSchools(accessToken),
        requestAdminUsers(accessToken)
      ]);
      setAdminWorkspace({
        schools: schoolsResult.schools ?? [],
        users: usersResult.users ?? []
      });
    } catch (requestError) {
      classifyAuthenticatedRequestError(requestError);
    } finally {
      setIsAdminLoading(false);
    }
  }

  async function handleCreateSchool(payload) {
    if (!accessToken) return;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await createAdminSchool(accessToken, payload);
      setAdminWorkspace((prev) => ({ ...prev, schools: result.schools ?? prev?.schools ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to create school.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleUpdateSchool(schoolId, payload) {
    if (!accessToken) return;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await updateAdminSchool(accessToken, schoolId, payload);
      setAdminWorkspace((prev) => ({ ...prev, schools: result.schools ?? prev?.schools ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to update school.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleDeleteSchool(schoolId) {
    if (!accessToken) return;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await deleteAdminSchool(accessToken, schoolId);
      setAdminWorkspace((prev) => ({ ...prev, schools: result.schools ?? prev?.schools ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to delete school.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleReseedSchool(schoolId) {
    if (!accessToken) return;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await reseedAdminSchool(accessToken, schoolId);
      setAdminWorkspace((prev) => ({ ...prev, schools: result.schools ?? prev?.schools ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to reseed demo data.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleUpdateUserPlatformRole(userId, role) {
    if (!accessToken) return;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await updateAdminUserRole(accessToken, userId, role);
      setAdminWorkspace((prev) => ({ ...prev, users: result.users ?? prev?.users ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to update user role.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleResetUserPassword(userId, tempPassword) {
    if (!accessToken) return null;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await resetAdminUserPassword(accessToken, userId, tempPassword);
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to reset password.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleAssignUserToSchool(userId, payload) {
    if (!accessToken) return null;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await assignAdminUserToSchool(accessToken, userId, payload);
      setAdminWorkspace((prev) => ({ ...prev, users: result.users ?? prev?.users ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to assign user to school.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleRemoveUserFromSchool(userId, schoolId) {
    if (!accessToken) return null;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await removeAdminUserFromSchool(accessToken, userId, schoolId);
      setAdminWorkspace((prev) => ({ ...prev, users: result.users ?? prev?.users ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to remove user from school.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleMoveUserToSchool(userId, payload) {
    if (!accessToken) return null;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await moveAdminUserToSchool(accessToken, userId, payload);
      setAdminWorkspace((prev) => ({ ...prev, users: result.users ?? prev?.users ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to move user to school.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleCreateAdminUser(payload) {
    if (!accessToken) return null;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await createAdminUserApi(accessToken, payload);
      setAdminWorkspace((prev) => ({ ...prev, users: result.users ?? prev?.users ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to create user.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleUpdateSchoolUserRole(userId, schoolId, role) {
    if (!accessToken) return null;
    setIsSavingAdmin(true);
    setAdminSaveError('');
    try {
      const result = await updateSchoolUserRoleApi(accessToken, userId, schoolId, role);
      setAdminWorkspace((prev) => ({ ...prev, users: result.users ?? prev?.users ?? [] }));
      return result;
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setAdminSaveError(requestError.message || 'Unable to update school user role.');
      }
      return null;
    } finally {
      setIsSavingAdmin(false);
    }
  }

  async function handleSelectSavedRun(runId) {
    const projectId = savedRunsWorkspace?.project?.id ?? activeProjectId;
    if (!accessToken || !projectId) {
      return;
    }

    navigate(`/saved-runs/${runId}`);
  }

  async function handleRenameRun(runId, newName) {
    const projectId = savedRunsWorkspace?.project?.id ?? activeProjectId;
    if (!accessToken || !projectId) {
      return;
    }

    const updatedWorkspace = await renameRun(accessToken, projectId, runId, { name: newName });
    setSavedRunsWorkspace(updatedWorkspace);
    analytics.track('run_renamed', { runId, newName });
  }

  async function handleDeleteRun(runId) {
    const projectId = savedRunsWorkspace?.project?.id ?? activeProjectId;
    if (!accessToken || !projectId) {
      return;
    }

    const updatedWorkspace = await deleteRun(accessToken, projectId, runId);
    setSavedRunsWorkspace(updatedWorkspace);
    analytics.track('run_deleted', { runId });
  }

  async function handleNavigateToRun(runId) {
    if (!accessToken || !activeProjectId) {
      return;
    }

    navigate(`/saved-runs/${runId}`);
  }

  async function handleSwitchProject(projectId) {
    if (!accessToken || !projectId || isSwitchingProject) {
      return;
    }

    setIsSwitchingProject(true);
    setIsProjectSwitcherOpen(false);
    resetWorkspaceState();

    try {
      const dashboardPayload = await refreshDashboard(accessToken, projectId);
      setDashboard(dashboardPayload);
      analytics.track('project_switched', { projectId });

      if (isRosterRoute && dashboardPayload.project?.id) {
        await loadRoster(accessToken, dashboardPayload.project.id);
      }
      if (isClassesRoute && dashboardPayload.project?.id) {
        await loadClasses(accessToken, dashboardPayload.project.id);
      }
      if (isGradesRoute && dashboardPayload.project?.id) {
        await loadGrades(accessToken, dashboardPayload.project.id);
      }
      if (isSavedRunsRoute && dashboardPayload.project?.id) {
        await loadSavedRuns(accessToken, dashboardPayload.project.id);
      }
      if (isFactorsRoute && dashboardPayload.project?.id) {
        await loadFactors(accessToken, dashboardPayload.project.id);
      }
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setError(requestError.message || 'Unable to switch to the selected project.');
      }
    } finally {
      setIsSwitchingProject(false);
    }
  }

  function handleOpenNewProjectDialog() {
    setIsNewProjectDialogOpen(true);
    setIsProjectSwitcherOpen(false);
    setNewProjectName('');
    setNewProjectSchoolYear('');
    setNewProjectSourceGrade('');
    setNewProjectTargetGrade('');
    setProjectError('');
  }

  function handleCloseNewProjectDialog() {
    if (isCreatingProject) {
      return;
    }
    setIsNewProjectDialogOpen(false);
    setProjectError('');
  }

  async function handleCreateProject(event) {
    event.preventDefault();
    if (!accessToken || isCreatingProject) {
      return;
    }

    setIsCreatingProject(true);
    setProjectError('');

    try {
      const result = await createProjectApi(accessToken, {
        name: newProjectName.trim(),
        schoolYear: newProjectSchoolYear.trim(),
        sourceGrade: newProjectSourceGrade.trim(),
        targetGrade: newProjectTargetGrade.trim()
      });

      setIsNewProjectDialogOpen(false);
      analytics.track('project_created', { name: newProjectName.trim(), schoolYear: newProjectSchoolYear.trim() });
      await loadProjects(accessToken);
      await handleSwitchProject(result.id);
      navigate('/');
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setProjectError(requestError.message || 'Unable to create the project.');
      }
    } finally {
      setIsCreatingProject(false);
    }
  }

  async function handleDeleteProject(projectId) {
    if (!accessToken || !projectId) return;
    try {
      const result = await deleteProjectApi(accessToken, projectId);
      setProjectsList(result.projects ?? []);
      if (dashboard?.project?.id === projectId) {
        resetWorkspaceState();
        const remaining = result.projects ?? [];
        if (remaining.length > 0) {
          await handleSwitchProject(remaining[0].id);
        }
      }
    } catch (requestError) {
      if (classifyAuthenticatedRequestError(requestError) === 'error') {
        setProjectError(requestError.message || 'Unable to delete the project.');
      }
    }
  }

  async function handleLogoutClick() {
    try {
      await logoutFromApi();
    } finally {
      analytics.track('logout');
      analytics.reset();
      clearStoredSession();
      setSearchTerm('');
      setActiveFilter('all');
      navigate('/', { replace: true });
    }
  }

  if (loading) {
    return (
      <div className="loading-shell">
        <div className="loading-card">
          <div className="spinner-ring" />
          <h1>Loading placement studio</h1>
          <p>Preparing your workspace, session, and latest project state.</p>
          <button
            type="button"
            className="loading-escape-link"
            onClick={() => { clearStoredSession(); setSearchTerm(''); setActiveFilter('all'); navigate('/', { replace: true }); }}
          >
            Taking too long? Sign out and try again
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="login-page">
        <div className="login-card">
          <div className="login-header">
            <h1 className="login-logo">SmartSort</h1>
            <p className="login-tagline">Intelligent class placement for schools and camps</p>
          </div>

          {/* Google Sign-In */}
          <a href="/api/auth/google" className="google-sign-in-btn">
            <svg width="18" height="18" viewBox="0 0 24 24">
              <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
              <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
              <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
              <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
            </svg>
            Sign in with Google
          </a>

          <div className="login-divider">
            <span>or</span>
          </div>

          {/* Magic Link */}
          {loginMode !== 'password' ? (
            magicLinkSent ? (
              <div className="login-magic-sent">
                <div className="login-magic-sent-icon">✉</div>
                <h3>Check your email</h3>
                <p>We sent a sign-in link to <strong>{magicLinkEmail}</strong>. Click the link in the email to sign in.</p>
                <button type="button" className="utility-button" onClick={() => { setMagicLinkSent(false); setMagicLinkEmail(''); }}>
                  Try a different email
                </button>
              </div>
            ) : (
              <form className="login-form" onSubmit={handleMagicLinkSubmit}>
                <label className="auth-field">
                  <span>Email address</span>
                  <input
                    type="email"
                    value={magicLinkEmail}
                    onChange={(event) => setMagicLinkEmail(event.target.value)}
                    placeholder="you@school.org"
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </label>

                {loginError && loginMode !== 'password' ? <div className="auth-error">{loginError}</div> : null}

                <button type="submit" className="primary-button auth-submit" disabled={isAuthenticating}>
                  {isAuthenticating ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isAuthenticating ? 'Sending...' : 'Send me a sign-in link'}
                </button>
              </form>
            )
          ) : null}

          {/* Password Sign-In (collapsible) */}
          {loginMode === 'password' ? (
            <form className="login-form" onSubmit={handleLoginSubmit}>
              <label className="auth-field">
                <span>Email</span>
                <input
                  type="email"
                  value={loginEmail}
                  onChange={(event) => setLoginEmail(event.target.value)}
                  placeholder="you@school.org"
                  autoComplete="username"
                  required
                  autoFocus
                />
              </label>

              <label className="auth-field">
                <span>Password</span>
                <input
                  type="password"
                  value={loginPassword}
                  onChange={(event) => setLoginPassword(event.target.value)}
                  placeholder="Enter your password"
                  autoComplete="current-password"
                  required
                />
              </label>

              {loginError ? <div className="auth-error">{loginError}</div> : null}

              <button type="submit" className="primary-button auth-submit" disabled={isAuthenticating}>
                {isAuthenticating ? <span className="button-spinner" aria-hidden="true" /> : null}
                {isAuthenticating ? 'Signing in...' : 'Sign in'}
              </button>
            </form>
          ) : null}

          <div className="login-toggle">
            {loginMode === 'password' ? (
              <button type="button" className="login-toggle-btn" onClick={() => { setLoginMode('default'); setMagicLinkEmail(loginEmail); setLoginError(''); }}>
                ← Back to magic link
              </button>
            ) : (
              <button type="button" className="login-toggle-btn" onClick={() => { setLoginMode('password'); setLoginEmail(magicLinkEmail); setLoginError(''); }}>
                Sign in with password instead
              </button>
            )}
          </div>

          <div className="login-footer">
            <span>SmartSort © {new Date().getFullYear()}</span>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="loading-shell">
        <div className="loading-card error-card">
          <h1>Dashboard unavailable</h1>
          <p>{error}</p>
          <button
            type="button"
            className="utility-button"
            style={{ marginTop: '1rem' }}
            onClick={() => {
              clearStoredSession();
              setUser(null);
              setAccessToken('');
              setDashboard(null);
              setError('');
            }}
          >
            Sign out
          </button>
        </div>
      </div>
    );
  }

  if (!dashboard && isSwitchingProject) {
    return (
      <div className="loading-shell">
        <div className="loading-card">
          <div className="spinner-ring" />
          <h1>Switching project</h1>
          <p>Loading the selected project.</p>
        </div>
      </div>
    );
  }

  if (!dashboard) {
    return (
      <div className="loading-shell">
        <div className="loading-card">
          <h1>No project found</h1>
          <p>Create a new placement project to get started, or switch to an existing one.</p>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
            <button
              type="button"
              className="primary-button"
              onClick={handleOpenNewProjectDialog}
            >
              + Create project
            </button>
            <button
              type="button"
              className="loading-escape-link"
              onClick={() => { clearStoredSession(); setSearchTerm(''); setActiveFilter('all'); navigate('/', { replace: true }); }}
            >
              Sign out
            </button>
          </div>
        </div>
        {isNewProjectDialogOpen ? (
          <div className="workspace-dialog-backdrop" onClick={handleCloseNewProjectDialog}>
            <div
              className="workspace-dialog panel"
              role="dialog"
              aria-modal="true"
              aria-labelledby="new-project-title"
              onClick={(event) => event.stopPropagation()}
            >
              <div className="panel-head compact">
                <div>
                  <span className="panel-kicker">New placement project</span>
                  <h2 id="new-project-title">Create a project</h2>
                </div>
                <button
                  type="button"
                  className="icon-button classes-dialog-close"
                  onClick={handleCloseNewProjectDialog}
                  aria-label="Close new project dialog"
                  disabled={isCreatingProject}
                >
                  ×
                </button>
              </div>

              {projectError ? <div className="auth-error">{projectError}</div> : null}

              <form className="roster-form" onSubmit={handleCreateProject}>
                <label className="auth-field">
                  <span>Project name</span>
                  <input
                    type="text"
                    value={newProjectName}
                    onChange={(event) => setNewProjectName(event.target.value)}
                    placeholder="e.g. Grade 5 to Grade 6 Placement"
                    required
                    autoFocus
                  />
                </label>

                <label className="auth-field">
                  <span>School year (optional)</span>
                  <input
                    type="text"
                    value={newProjectSchoolYear}
                    onChange={(event) => setNewProjectSchoolYear(event.target.value)}
                    placeholder="e.g. 2026-2027"
                  />
                </label>

                <label className="auth-field">
                  <span>Source grade (optional)</span>
                  <input
                    type="text"
                    value={newProjectSourceGrade}
                    onChange={(event) => setNewProjectSourceGrade(event.target.value)}
                    placeholder="e.g. 5"
                  />
                </label>

                <label className="auth-field">
                  <span>Target grade (optional)</span>
                  <input
                    type="text"
                    value={newProjectTargetGrade}
                    onChange={(event) => setNewProjectTargetGrade(event.target.value)}
                    placeholder="e.g. 6"
                  />
                </label>

                <div className="roster-form-actions">
                  <button
                    type="button"
                    className="utility-button"
                    onClick={handleCloseNewProjectDialog}
                    disabled={isCreatingProject}
                  >
                    Cancel
                  </button>
                  <button type="submit" className="primary-button" disabled={isCreatingProject}>
                    {isCreatingProject ? <span className="button-spinner" aria-hidden="true" /> : null}
                    {isCreatingProject ? 'Creating project' : 'Create project'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : null}
      </div>
    );
  }


  return (
    <div className="app-shell">
      {generationError ? (
        <div className="preview-bar error-bar">
          <span className="preview-pill">Generation issue</span>
          <span>{generationError}</span>
        </div>
      ) : null}

      {rosterError ? (
        <div className="preview-bar error-bar">
          <span className="preview-pill">Students issue</span>
          <span>{rosterError}</span>
        </div>
      ) : null}

      {savedRunsError ? (
        <div className="preview-bar error-bar">
          <span className="preview-pill">Saved runs issue</span>
          <span>{savedRunsError}</span>
        </div>
      ) : null}

      {classesError ? (
        <div className="preview-bar error-bar">
          <span className="preview-pill">Classes issue</span>
          <span>{classesError}</span>
        </div>
      ) : null}

      {factorsError ? (
        <div className="preview-bar error-bar">
          <span className="preview-pill">Factors issue</span>
          <span>{factorsError}</span>
        </div>
      ) : null}

      <aside className="sidebar">
        <div className="sidebar-header">
          <div>
            <h2>{dashboard.school.name}</h2>
          </div>
        </div>

        <div className="project-switcher-wrap" ref={projectSwitcherRef}>
          <button
            className="workspace-card workspace-card-button"
            type="button"
            onClick={() => setIsProjectSwitcherOpen((prev) => !prev)}
          >
            <p className="workspace-label">{dashboard.school.workspaceLabel}</p>
            <strong>{dashboard.project.name}</strong>
            {dashboard.project.schoolYear ? <span>{dashboard.project.schoolYear}</span> : null}
          </button>

          {isProjectSwitcherOpen ? (
            <div className="project-switcher-dropdown">
              <div className="project-switcher-header">
                <span className="project-switcher-title">Projects</span>
                <button
                  type="button"
                  className="primary-button project-switcher-new-btn"
                  onClick={handleOpenNewProjectDialog}
                >
                  + New
                </button>
              </div>
              {projectsList.length === 0 ? (
                <p className="project-switcher-empty">No projects found.</p>
              ) : (
                <ul className="project-switcher-list">
                  {projectsList.map((project) => (
                    <li key={project.id}>
                      <button
                        type="button"
                        className={`project-switcher-item ${project.id === dashboard.project.id ? 'is-active' : ''}`}
                        onClick={() => handleSwitchProject(project.id)}
                        disabled={isSwitchingProject || project.id === dashboard.project.id}
                      >
                        <strong>{project.name}</strong>
                        <span className="project-switcher-meta">
                          {[project.schoolYear, `${project.studentCount} ${terminology.students.toLowerCase()}`, `${project.classCount} ${terminology.classes.toLowerCase()}`].filter(Boolean).join(' · ')}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ) : null}
        </div>

        <nav className="sidebar-nav">
          {navigationSections.map((section, sectionIndex) => (
            <React.Fragment key={section.label ?? sectionIndex}>
              {section.label ? (
                <div className="sidebar-section-label">{section.label}</div>
              ) : null}
              {section.items.filter((item) => !item.permission || user?.permissions?.includes(item.permission)).map((item) => {
                const navTo = item.to === '/classes' && terminology.class === 'Bunk' ? '/bunks' : item.to;
                return (
                <NavLink
                  key={item.label}
                  to={navTo}
                  end={navTo === '/'}
                  className={({ isActive }) => `nav-item ${isActive ? 'is-active' : ''}`}
                >
                  <span className="nav-item-icon" style={{ color: item.color, backgroundColor: `${item.color}14` }}>
                    <item.icon />
                  </span>
                  <span>{item.label === 'Students' ? terminology.students : item.label === 'Classes' ? terminology.classes : item.label}</span>
                </NavLink>
                );
              })}
            </React.Fragment>
          ))}
        </nav>

      </aside>

      <div className="main-shell">
        <header className="topbar">
          <div className="search-wrap">
            <span className="search-icon">⌕</span>
            <input
              className="topbar-search"
              type="search"
              value={searchTerm}
              onChange={(event) => setSearchTerm(event.target.value)}
              placeholder={
                isRosterRoute
                  ? `Search ${terminology.students.toLowerCase()}, IDs, homerooms, suggested ${terminology.classes.toLowerCase()}`
                  : isSavedRunsRoute
                    ? `Search run names, ${terminology.students.toLowerCase()}, ${terminology.classes.toLowerCase()}`
                  : isClassesRoute
                    ? `Search ${terminology.classes.toLowerCase()}`
                  : isGradesRoute
                    ? 'Search grades'
                  : isFactorsRoute
                    ? 'Search factors'
                  : isOverviewRoute
                    ? `Search ${terminology.students.toLowerCase()}`
                  : isUsersRoute
                    ? 'Search users'
                  : isAdminRoute
                    ? 'Search admin'
                  : 'Search projects'
              }
            />
          </div>

          <div className="topbar-actions">
            <div className="avatar-menu-wrap" ref={avatarMenuRef}>
              <button
                type="button"
                className="utility-button strong"
                onClick={() => setIsAvatarMenuOpen((open) => !open)}
                aria-label="User menu"
              >
                {getInitials(user.displayName)}
              </button>
              {isAvatarMenuOpen ? (
                <div className="avatar-menu-popover" onClick={() => setIsAvatarMenuOpen(false)}>
                  <div className="avatar-menu-header">
                    <strong>{user.displayName}</strong>
                    <span>{user.email}</span>
                  </div>
                  <button type="button" className="avatar-menu-item" onClick={() => { setIsAvatarMenuOpen(false); setShowTutorial(true); }}>
                    Replay tutorial
                  </button>
                  <a href="/guide" target="_blank" rel="noopener noreferrer" className="avatar-menu-item" style={{ textDecoration: 'none' }}>
                    View guide
                  </a>
                  <button type="button" className="avatar-menu-item" onClick={handleLogoutClick}>
                    Sign out
                  </button>
                </div>
              ) : null}
            </div>
          </div>
        </header>

        <main className="dashboard">
          <Routes>
            <Route
              path="/"
              element={
                <HomeView
                  dashboard={dashboard}
                  projectsList={projectsList}
                  onSwitchProject={handleSwitchProject}
                  onDeleteProject={handleDeleteProject}
                  onCreateProject={handleOpenNewProjectDialog}
                  onOpenOverview={() => navigate('/overview')}
                  terminology={terminology}
                />
              }
            />
            <Route
              path="/overview"
              element={
                <OverviewView
                  dashboard={dashboard}
                  filteredStudents={filteredStudents}
                  filteredClasses={filteredClasses}
                  availableGrades={availableGrades}
                  selectedGrade={effectiveGrade}
                  onGradeChange={setSelectedGrade}
                  filterOptions={filterOptions.map((opt) => opt.value === 'all' ? { ...opt, label: `All ${terminology.students.toLowerCase()}` } : opt)}
                  activeFilter={activeFilter}
                  onFilterChange={setActiveFilter}
                  onGenerateClick={handleOpenGenerateDialog}
                  onOpenRoster={() => navigate('/roster')}
                  onSelectRun={handleNavigateToRun}
                  isGenerating={isGenerating}
                  terminology={terminology}
                  onUpdateProjectSettings={handleUpdateProjectSettings}
                  permissions={user?.permissions ?? []}
                  onAssignStudent={handleAssignStudent}
                  onToggleLock={handleToggleLock}
                  onToggleBehavior={handleToggleBehavior}
                  onApplyRunAssignments={handleApplyRunAssignments}
                />
              }
            />
            <Route
              path="/roster"
              element={
                <RosterView
                  roster={roster}
                  searchTerm={searchTerm}
                  isLoading={isRosterLoading}
                  isSaving={isSavingRoster}
                  saveError={rosterSaveError}
                  onSaveStudent={handleSaveRosterStudent}
                  onCreateStudent={handleCreateRosterStudent}
                  onDeleteStudent={handleDeleteRosterStudent}
                  onImportRoster={handleImportRoster}
                  onUpdateStudentRelationships={handleUpdateStudentRelationships}
                  terminology={terminology}
                  permissions={user?.permissions ?? []}
                />
              }
            />
            <Route
              path="/saved-runs"
              element={
                <SavedRunsView
                  savedRunsWorkspace={savedRunsWorkspace}
                  searchTerm={searchTerm}
                  isLoading={isSavedRunsLoading}
                  onSelectRun={handleSelectSavedRun}
                  onRenameRun={handleRenameRun}
                  onDeleteRun={handleDeleteRun}
                  onApplyRunAssignments={handleApplyRunAssignments}
                  onRefreshRun={(runId) => loadSavedRuns(accessToken, activeProjectId, runId)}
                  terminology={terminology}
                />
              }
            />
            <Route
              path="/saved-runs/:runId"
              element={
                <SavedRunsView
                  savedRunsWorkspace={savedRunsWorkspace}
                  searchTerm={searchTerm}
                  isLoading={isSavedRunsLoading}
                  onSelectRun={handleSelectSavedRun}
                  onRenameRun={handleRenameRun}
                  onDeleteRun={handleDeleteRun}
                  onApplyRunAssignments={handleApplyRunAssignments}
                  onRefreshRun={(runId) => loadSavedRuns(accessToken, activeProjectId, runId)}
                  terminology={terminology}
                />
              }
            />
            <Route
              path="/classes"
              element={
                <ClassesView
                  classesWorkspace={classesWorkspace}
                  searchTerm={searchTerm}
                  isLoading={isClassesLoading}
                  isSaving={isSavingClasses}
                  saveError={classesSaveError}
                  onCreateClass={handleCreateProjectClass}
                  onSaveClass={handleSaveProjectClass}
                  onDeleteClass={handleDeleteProjectClass}
                  terminology={terminology}
                  permissions={user?.permissions ?? []}
                />
              }
            />
            <Route
              path="/bunks"
              element={
                <ClassesView
                  classesWorkspace={classesWorkspace}
                  searchTerm={searchTerm}
                  isLoading={isClassesLoading}
                  isSaving={isSavingClasses}
                  saveError={classesSaveError}
                  onCreateClass={handleCreateProjectClass}
                  onSaveClass={handleSaveProjectClass}
                  onDeleteClass={handleDeleteProjectClass}
                  terminology={terminology}
                  permissions={user?.permissions ?? []}
                />
              }
            />
            <Route
              path="/factors"
              element={
                <FactorsView
                  factorsWorkspace={factorsWorkspace}
                  isLoading={isFactorsLoading}
                  isSaving={isSavingFactors}
                  saveError={factorsSaveError}
                  onCreateFactor={handleCreateFactor}
                  onUpdateFactor={handleUpdateFactor}
                  onDeleteFactor={handleDeleteFactor}
                  onUpdateFactorSetting={handleUpdateFactorSetting}
                  terminology={terminology}
                  permissions={user?.permissions ?? []}
                />
              }
            />
            <Route
              path="/users"
              element={
                <UsersView
                  usersWorkspace={usersWorkspace}
                  isLoading={isUsersLoading}
                  isSaving={isSavingUser}
                  saveError={userSaveError}
                  onCreateUser={handleCreateUser}
                  onUpdateUser={handleUpdateUser}
                  onRemoveUser={handleRemoveUser}
                  terminology={terminology}
                />
              }
            />
            <Route
              path="/admin"
              element={
                user?.platformRole === 'platform_admin' ? (
                  <AdminView
                    adminWorkspace={adminWorkspace}
                    isLoading={isAdminLoading}
                    isSaving={isSavingAdmin}
                    saveError={adminSaveError}
                    onCreateSchool={handleCreateSchool}
                    onUpdateSchool={handleUpdateSchool}
                    onDeleteSchool={handleDeleteSchool}
                    onReseedSchool={handleReseedSchool}
                    onUpdateUserRole={handleUpdateUserPlatformRole}
                    onResetPassword={handleResetUserPassword}
                    onAssignToSchool={handleAssignUserToSchool}
                    onRemoveFromSchool={handleRemoveUserFromSchool}
                    onMoveToSchool={handleMoveUserToSchool}
                    onCreateUser={handleCreateAdminUser}
                    onUpdateSchoolUserRole={handleUpdateSchoolUserRole}
                  />
                ) : (
                  <Navigate to="/" replace />
                )
              }
            />
            <Route path="/school" element={<Navigate to="/" replace />} />
            <Route
              path="/grades"
              element={
                <GradesView
                  gradesWorkspace={gradesWorkspace}
                  searchTerm={searchTerm}
                  isLoading={isGradesLoading}
                  isSaving={isSavingGrades}
                  saveError={gradesSaveError}
                  onCreateGrade={handleCreateGrade}
                  onSaveGrade={handleSaveGrade}
                  onDeleteGrade={handleDeleteGrade}
                  terminology={terminology}
                  permissions={user?.permissions ?? []}
                />
              }
            />
            {placeholderRoutes.map((route) => (
              <Route
                key={route.path}
                path={route.path}
                element={<PlaceholderView title={route.title} description={route.description} />}
              />
            ))}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </main>
      </div>

      {isNewProjectDialogOpen ? (
        <div className="workspace-dialog-backdrop" onClick={handleCloseNewProjectDialog}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="new-project-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">New placement project</span>
                <h2 id="new-project-title">Create a project</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseNewProjectDialog}
                aria-label="Close new project dialog"
                disabled={isCreatingProject}
              >
                ×
              </button>
            </div>

            {projectError ? <div className="auth-error">{projectError}</div> : null}

            <form className="roster-form" onSubmit={handleCreateProject}>
              <label className="auth-field">
                <span>Project name</span>
                <input
                  type="text"
                  value={newProjectName}
                  onChange={(event) => setNewProjectName(event.target.value)}
                  placeholder="e.g. Grade 5 to Grade 6 Placement"
                  required
                  autoFocus
                />
              </label>

              <label className="auth-field">
                <span>School year (optional)</span>
                <input
                  type="text"
                  value={newProjectSchoolYear}
                  onChange={(event) => setNewProjectSchoolYear(event.target.value)}
                  placeholder="e.g. 2026-2027"
                />
              </label>

              <label className="auth-field">
                <span>Source grade (optional)</span>
                <input
                  type="text"
                  value={newProjectSourceGrade}
                  onChange={(event) => setNewProjectSourceGrade(event.target.value)}
                  placeholder="e.g. 5"
                />
              </label>

              <label className="auth-field">
                <span>Target grade (optional)</span>
                <input
                  type="text"
                  value={newProjectTargetGrade}
                  onChange={(event) => setNewProjectTargetGrade(event.target.value)}
                  placeholder="e.g. 6"
                />
              </label>

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCloseNewProjectDialog}
                  disabled={isCreatingProject}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isCreatingProject}>
                  {isCreatingProject ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isCreatingProject ? 'Creating project' : 'Create project'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {solverBlockerMessage ? (
        <div className="dialog-overlay" onClick={() => setSolverBlockerMessage('')}>
          <div className="dialog-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '420px', textAlign: 'center' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
            <h2 style={{ margin: '0 0 12px', fontSize: '18px' }}>Cannot Run Solver</h2>
            <p style={{ color: '#64748b', fontSize: '14px', lineHeight: '1.6', margin: '0 0 20px' }}>{solverBlockerMessage}</p>
            <button
              type="button"
              className="primary-button"
              onClick={() => setSolverBlockerMessage('')}
              style={{ minWidth: '100px', textAlign: 'center', display: 'inline-flex', justifyContent: 'center' }}
            >
              OK
            </button>
          </div>
        </div>
      ) : null}

      {isGenerateDialogOpen ? (
        <div className="workspace-dialog-backdrop" onClick={handleCloseGenerateDialog}>
          <div
            className="workspace-dialog panel"
            role="dialog"
            aria-modal="true"
            aria-labelledby="generate-run-title"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="panel-head compact">
              <div>
                <span className="panel-kicker">New proposal</span>
                <h2 id="generate-run-title">Name this run</h2>
              </div>
              <button
                type="button"
                className="icon-button classes-dialog-close"
                onClick={handleCloseGenerateDialog}
                aria-label="Close generate proposal dialog"
                disabled={isGenerating}
              >
                ×
              </button>
            </div>

            {generationError ? <div className="auth-error">{generationError}</div> : null}

            <form className="roster-form" onSubmit={(event) => {
              event.preventDefault();
              void handleGenerateClick();
            }}
            >
              <label className="auth-field">
                <span>Run name</span>
                <input
                  type="text"
                  value={runNameDraft}
                  onChange={(event) => setRunNameDraft(event.target.value)}
                  placeholder="Leave blank to auto-name the run"
                  autoFocus
                />
              </label>

              <p className="editor-note">
                If blank, the server will create a unique proposal name automatically.
              </p>

              <div className="roster-form-actions">
                <button
                  type="button"
                  className="utility-button"
                  onClick={handleCloseGenerateDialog}
                  disabled={isGenerating}
                >
                  Cancel
                </button>
                <button type="submit" className="primary-button" disabled={isGenerating}>
                  {isGenerating ? <span className="button-spinner" aria-hidden="true" /> : null}
                  {isGenerating ? 'Starting proposal' : 'Generate proposal'}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showTutorial ? (
        <TutorialOverlay
          terminology={terminology}
          onComplete={handleTutorialComplete}
        />
      ) : null}

      {user && dashboard ? (
        <ChatBubble
          accessToken={accessToken}
          schoolType={dashboard.school?.schoolType}
        />
      ) : null}
    </div>
  );
}

function getInitials(displayName) {
  return displayName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() || '')
    .join('');
}

export default App;
